import { CatalogSettings, ProgramCreditAddLicenseBlock } from '@/@types/content';
import {
  LiveEcomLabels,
  MembershipCartText,
  OnDemandEcomLabels,
  ProgramCreditLabels,
  OverviewOnDemandVersionText,
  OverviewPlaceholderBodyText,
  OverviewPlaceholderHeaderText,
  ProgramExpiredLabels,
  PublicationEcomLabels
} from '@/components/ui/PDP/labels';
import { useGetGlobalElementQuery } from '@/redux/api/contentDeliveryAPI';
import { simpleHash } from '@/utils/helpers';

export type OtherLabels = {
  membershipCartText: string;
  overviewPlaceholderHeaderText: string;
  overviewPlaceholderBodyText: string;
  overviewOnDemandVersionText: string;
  bulkDiscountQuantity?: number;
};

export const useCatalogLabels = (disabled = false) => {
  const { data: labelsData, isLoading } = useGetGlobalElementQuery(
    {
      name: 'catalog-labels',
      userId: simpleHash('ANON')
    },
    { skip: disabled }
  );

  const cmsLabels = labelsData as CatalogSettings & {
    programCreditAddLicenseBlocks?: ProgramCreditAddLicenseBlock[];
  };

  const programCreditAddLicenseCopy = cmsLabels?.programCreditAddLicenseBlocks?.[0];

  const cmsEnrichedLiveLabels = {
    ...LiveEcomLabels,
    registerButton: cmsLabels?.registerButtonText ?? LiveEcomLabels.registerButton,
    purchaseButton: cmsLabels?.programAddToCartButtonText ?? LiveEcomLabels.purchaseButton,
    launchButton: cmsLabels?.launchButtonText ?? LiveEcomLabels.launchButton,
    privilegedMembers:
      cmsLabels?.privilegedMembershipAdviceText ?? LiveEcomLabels.privilegedMembers,
    seciWsDiscountAdvice:
      cmsLabels?.seciWsDiscountAdviceText ?? LiveEcomLabels.seciWsDiscountAdvice,
    extraMessage: cmsLabels?.additionalAdviceText,
    materials: cmsLabels?.accessMaterialsButtonText ?? LiveEcomLabels.materials,
    addToCalendar: cmsLabels?.addToCalendarButtonText ?? LiveEcomLabels.addToCalendar,
    ondemandCrosslink: cmsLabels?.onDemandCrosslink ?? LiveEcomLabels.ondemandCrosslink
  };

  const cmsEnrichedOnDemandLabels = {
    ...OnDemandEcomLabels,
    launchButton: cmsLabels?.launchButtonText ?? OnDemandEcomLabels.launchButton,
    purchaseButton: cmsLabels?.programAddToCartButtonText ?? OnDemandEcomLabels.purchaseButton,
    privilegedMembers:
      cmsLabels?.privilegedMembershipAdviceText ?? OnDemandEcomLabels.privilegedMembers,
    seciWsDiscountAdvice:
      cmsLabels?.seciWsDiscountAdviceText ?? OnDemandEcomLabels.seciWsDiscountAdvice,
    extraMessage: cmsLabels?.additionalAdviceText,
    materials: cmsLabels?.accessMaterialsButtonText ?? OnDemandEcomLabels.materials,
    upcomingLiveCrosslink:
      cmsLabels?.upcomingLiveCrosslink ?? OnDemandEcomLabels.upcomingLiveCrosslink,
    liveNowCrosslink: cmsLabels?.liveNowCrosslink ?? OnDemandEcomLabels.liveNowCrosslink,
    poecExpiringSoonDayRange: cmsLabels?.poecExpiringSoonDayRange ?? 14,
    poecExtensionUrl: cmsLabels?.poecExtensionUrl ?? '',
    poecExtendAccessPurchaseButton:
      cmsLabels?.poecExtensionPurchaseButtonLabel ??
      OnDemandEcomLabels.poecExtendAccessPurchaseButton,
    poecExtendAccessToast:
      cmsLabels?.poecExtensionPurchaseToast ?? OnDemandEcomLabels.poecExtendAccessToast,
    poecExtensionFallbackUrl: cmsLabels?.poecExtensionFallbackUrl
  };

  const cmsEnrichedProgramExpiredLabels = {
    ...ProgramExpiredLabels,
    headingProgram: cmsLabels?.expiredProgramHeader ?? ProgramExpiredLabels.headingProgram,
    bodyProgram: cmsLabels?.expiredProgramBody ?? ProgramExpiredLabels.bodyProgram,
    exploreButtonLabel:
      cmsLabels?.expiredProgramExploreButtonText ?? ProgramExpiredLabels.exploreButtonLabel,
    exploreButtonUrl:
      cmsLabels?.expiredProgramExploreButtonUrl ?? ProgramExpiredLabels.exploreButtonUrl
  };

  const cmsEnrichedPublicationEcomLabels = {
    ...PublicationEcomLabels,
    purchaseButton:
      cmsLabels?.publicationAddToCartButtonText ?? PublicationEcomLabels.purchaseButton,
    preorderPurchaseButton:
      cmsLabels?.publicationPreorderButtonText ?? PublicationEcomLabels.preorderPurchaseButton,
    readButton: cmsLabels?.readOnPlusButtonText ?? PublicationEcomLabels.readButton,
    upkeepAvailableTooltipTitle:
      cmsLabels?.upkeepServiceAvailableTooltipLabel ??
      PublicationEcomLabels.upkeepAvailableTooltipTitle,
    upkeepAvailableTooltipContent:
      cmsLabels?.upkeepServiceAvailableTooltipText ??
      PublicationEcomLabels.upkeepAvailableTooltipContent,
    internationalShippingTooltipTitle:
      cmsLabels?.internationalShippingWarningTooltipLabel ??
      PublicationEcomLabels.internationalShippingTooltipTitle,
    internationalShippingTooltipContent:
      cmsLabels?.internationalShippingWarningTooltipText ??
      PublicationEcomLabels.internationalShippingTooltipContent,
    plusToutRichText: cmsLabels?.plusToutRichText ?? PublicationEcomLabels.plusToutRichText
  };

  const featuredBooksBundleConfig = {
    headline: cmsLabels?.featuredPublicationsMultipleHeadline,
    promoCode: cmsLabels?.featuredPublicationsPromoCode,
    sourceCode: cmsLabels?.featuredPublicationsSourceCode,
    keyCode: cmsLabels?.featuredPublicationsKeyCode,
    addToCartLabel: cmsLabels?.featuredPublicationsAddToCartLabel
  };

  const cmsEnrichedProgramCreditLabels = {
    ...ProgramCreditLabels,
    addLicenseBlockHeading:
      programCreditAddLicenseCopy?.heading ?? ProgramCreditLabels.addLicenseBlockHeading,
    addLicenseBlockSubheading:
      programCreditAddLicenseCopy?.subheading ?? ProgramCreditLabels.addLicenseBlockSubheading
  };

  const otherLabels: OtherLabels = {
    membershipCartText: cmsLabels?.membershipCartLabel ?? MembershipCartText,
    overviewPlaceholderHeaderText:
      cmsLabels?.overviewPlaceholderHeaderText ?? OverviewPlaceholderHeaderText,
    overviewPlaceholderBodyText:
      cmsLabels?.overviewPlaceholderBodyText ?? OverviewPlaceholderBodyText,
    overviewOnDemandVersionText:
      cmsLabels?.overviewOnDemandVersionText ?? OverviewOnDemandVersionText,
    bulkDiscountQuantity: cmsLabels?.bulkDiscountQuantity
  };

  return {
    liveEcomLabels: cmsEnrichedLiveLabels,
    onDemandEcomLabels: cmsEnrichedOnDemandLabels,
    programExpiredLabels: cmsEnrichedProgramExpiredLabels,
    publicationEcomLabels: cmsEnrichedPublicationEcomLabels,
    programCreditLabels: cmsEnrichedProgramCreditLabels,
    otherLabels,
    twelveHourTimezones: cmsLabels?.timeZoneAbbreviationsFor12Hour ?? [],
    isLoading,
    featuredBooksBundleConfig
  };
};

export default useCatalogLabels;
